// src/pages/public/SelectStaffPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { db } from "../../config/firebase";

/** --- simple hook for phone detection --- **/
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );

  useEffect(() => {
    const handler = () => {
      if (typeof window === "undefined") return;
      setIsMobile(window.innerWidth <= breakpoint);
    };
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);

  return isMobile;
}

export default function SelectStaffPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sp] = useSearchParams();
  const isMobile = useIsMobile();

  // ---- read from router state (preferred) ----
  const stateServices = location.state?.services || [];
  const stateTotals = location.state?.totals || null;

  // ---- optional fallback if user reloads this page directly ----
  const qpTotals = {
    count: Number(sp.get("count") || 0),
    totalPrice: Number(sp.get("total") || 0),
    totalMin: Number(sp.get("dur") || 0),
  };

  const services = stateServices;
  const totals = stateTotals ?? qpTotals;

  // derived labels
  const totalPriceLabel =
    stateTotals?.totalPriceLabel ?? formatCAD(totals.totalPrice || 0);
  const totalDurLabel =
    stateTotals?.totalDurLabel ?? formatMinutes(totals.totalMin || 0);

  const hero =
    "https://assets.api.uizard.io/api/cdn/stream/597fae2c-5bf0-46f2-ac43-e83205eb5d64.png";

  const [staff, setStaff] = useState([]);
  const [selected, setSelected] = useState("");
  const [noPref, setNoPref] = useState(false);

  useEffect(() => {
    const run = async () => {
      try {
        const snap = await getDocs(collection(db, "employees"));
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        setStaff(rows);
        if (rows.length) setSelected(rows[0].id);
      } catch (e) {
        console.error("fetch staff error:", e);
      }
    };
    run();
  }, []);

  // If nothing was selected on previous page, you could redirect back if you want
  useEffect(() => {
    if (!totals?.count) {
      // navigate("/booking");
    }
  }, [totals?.count, navigate]);

  const canProceed = noPref || !!selected;

  const goNext = () => {
    if (!canProceed) return;
    const staffId = noPref ? "any" : selected;

    navigate("/select-time", {
      state: {
        staffId,
        services, // full array of chosen services
        totals: {
          count: totals.count,
          totalPrice: totals.totalPrice,
          totalMin: totals.totalMin,
          totalPriceLabel,
          totalDurLabel,
        },
      },
    });
  };

  const summary = useMemo(
    () => ({
      total: totalPriceLabel,
      services: totals.count || 0,
      duration: totalDurLabel,
    }),
    [totalPriceLabel, totalDurLabel, totals.count]
  );

  // responsive containers
  const heroBoxStyle = isMobile ? sx.heroBoxMobile : sx.heroBoxDesktop;
  const containerStyle = isMobile ? sx.containerMobile : sx.containerDesktop;
  const sectionTitleStyle = isMobile
    ? { ...sx.sectionTitle, margin: "12px 0 16px" }
    : sx.sectionTitle;
  const bottomBarStyle = isMobile ? sx.bottomBarMobile : sx.bottomBarDesktop;

  return (
    <div style={sx.screen}>
      {/* Header lives in AppRouter */}

      {/* Hero */}
      <div style={heroBoxStyle}>
        <div style={{ ...sx.heroImage, backgroundImage: `url(${hero})` }} />
        <div
          style={{
            ...sx.heroOverlay,
            ...(isMobile ? sx.heroOverlayMobile : {}),
          }}
        >
          <div style={{ display: "grid", gap: 6 }}>
            <div style={sx.heroTitle}>613Nail Hub</div>
            <div style={sx.heroMeta}>
            📍 598 Rideau St, Ottawa, ON K1N 6C6 <br></br> ☎︎ (613) 327-4049
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={containerStyle}>
        <div style={sectionTitleStyle}>Select staff</div>

        <div style={{ ...sx.card, ...(isMobile ? sx.cardMobile : {}) }}>
          {/* No preference */}
          <button
            onClick={() => {
              setNoPref(true);
              setSelected("");
            }}
            style={{ ...sx.noPrefRow, ...(noPref ? sx.noPrefRowActive : {}) }}
          >
            <div style={sx.noPrefAvatar}>👤</div>
            <div style={{ display: "grid", gap: 2, textAlign: "left" }}>
              <div style={sx.noPrefTitle}>No preference</div>
              <div style={sx.noPrefSub}>Maximum availability</div>
            </div>
            <span style={sx.chev}>›</span>
          </button>

          {/* Staff list */}
          {staff.map((s) => {
            const isActive = !noPref && selected === s.id;
            const name = s.name || s.id;
            return (
              <button
                key={s.id}
                onClick={() => {
                  setNoPref(false);
                  setSelected(s.id);
                }}
                style={{ ...sx.staffRow, ...(isActive ? sx.staffRowActive : {}) }}
              >
                <div style={{ ...sx.initial, ...(isActive ? sx.initialActive : {}) }}>
                  {name.slice(0, 1).toUpperCase()}
                </div>
                <div
                  style={{
                    ...sx.staffName,
                    ...(isActive ? sx.staffNameActive : {}),
                  }}
                >
                  {name}
                </div>
                <span
                  style={{
                    ...sx.chev,
                    ...(isActive ? sx.chevActive : {}),
                  }}
                >
                  ›
                </span>
              </button>
            );
          })}
        </div>

        {/* Bottom summary */}
        <div style={bottomBarStyle}>
          <div style={sx.summary}>
            <div style={sx.dot} /> <span>{summary.total}</span>
            <div style={{ width: 12 }} />
            <div style={sx.dot} />{" "}
            <span>
              {summary.services} {summary.services === 1 ? "service" : "services"} —{" "}
              {summary.duration}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              gap: 8,
              ...(isMobile
                ? { width: "100%", flexDirection: "column-reverse" }
                : {}),
            }}
          >
            <button
              style={{
                ...sx.backBtn,
                ...(isMobile ? { width: "100%", textAlign: "center" } : {}),
              }}
              onClick={() => navigate(-1)}
            >
              ← Back
            </button>
            <button
              style={{
                ...sx.nextBtn,
                ...(isMobile ? { width: "100%" } : {}),
                opacity: canProceed ? 1 : 0.7,
                cursor: canProceed ? "pointer" : "not-allowed",
              }}
              disabled={!canProceed}
              onClick={goNext}
            >
              Next →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* helpers */
function formatCAD(n) {
  if (!isFinite(n)) return "C$ 0";
  try {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `C$ ${n}`;
  }
}
function formatMinutes(min) {
  if (!min || !isFinite(min)) return "0m";
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}

/* ---------------- Styles ---------------- */
const sx = {
  screen: {
    backgroundColor: "#f3f6fa",
    minHeight: "100vh",
    fontFamily:
      "Work Sans, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },

  // hero wrappers
  heroBoxDesktop: {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "0 20px",
    position: "relative",
  },
  heroBoxMobile: {
    maxWidth: 480,
    margin: "0 auto",
    padding: "0 16px",
    position: "relative",
  },

  heroImage: {
    width: "100%",
    height: 210,
    borderRadius: 4,
    backgroundSize: "cover",
    backgroundPosition: "center",
  },
  heroOverlay: {
    position: "absolute",
    left: 40,
    top: 64,
    display: "flex",
    gap: 12,
    alignItems: "center",
    background: "rgba(113,93,89,0.85)",
    padding: "10px 14px",
    borderRadius: 10,
    color: "#fff",
  },
  heroOverlayMobile: {
    left: 16,
    top: 40,
    right: 16,
  },
  badgeNum: { fontSize: 36, fontWeight: 300, lineHeight: 1, padding: "6px 10px" },
  heroTitle: { fontSize: 18, fontWeight: 700 },
  heroMeta: { fontSize: 13, fontWeight: 500, opacity: 0.95 },

  // containers
  containerDesktop: {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "18px 20px 40px",
  },
  containerMobile: {
    maxWidth: 480,
    margin: "0 auto",
    padding: "18px 16px 32px",
  },

  sectionTitle: {
    fontSize: 28,
    fontWeight: 600,
    color: "#101827",
    margin: "12px 200px 16px",
  },

  card: {
    background: "#ffffff",
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    padding: 0,
    maxWidth: 800,
    overflow: "hidden",
    margin: "0 auto",
  },
  cardMobile: {
    maxWidth: "100%",
  },

  // No preference row
  noPrefRow: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "auto 1fr auto",
    gap: 12,
    alignItems: "center",
    padding: "14px 16px",
    background: "#fff",
    border: "none",
    cursor: "pointer",
    textAlign: "left",
  },
  noPrefRowActive: { background: "#f5f5fb" },
  noPrefAvatar: {
    width: 40,
    height: 40,
    borderRadius: 999,
    background: "#E8F0FB",
    display: "grid",
    placeItems: "center",
    fontSize: 20,
  },
  noPrefTitle: { fontSize: 16, fontWeight: 800, color: "#030303" },
  noPrefSub: { fontSize: 13, fontWeight: 500, color: "#6b7280" },

  // Staff rows
  staffRow: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "auto 1fr auto",
    gap: 12,
    alignItems: "center",
    padding: 12,
    border: "none",
    background: "#fff",
    cursor: "pointer",
    textAlign: "left",
    borderTop: "1px solid #eef0f3",
  },
  staffRowActive: { background: "#5c5470" },
  initial: {
    width: 48,
    height: 48,
    borderRadius: 7,
    background: "#c9ada7",
    color: "#5c5470",
    display: "grid",
    placeItems: "center",
    fontWeight: 700,
    fontSize: 28,
  },
  initialActive: { background: "#c9ada7", color: "#5c5470" },
  staffName: { fontSize: 17, fontWeight: 700, color: "#030303" },
  staffNameActive: { color: "#ffffff" },
  chev: { color: "#9aa0a6", fontSize: 18, paddingRight: 6 },
  chevActive: { color: "#EAF0FF" },

  // bottom bar
  bottomBarDesktop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    maxWidth: 800,
    width: "100%",
    paddingTop: 12,
    margin: "0 auto",
  },
  bottomBarMobile: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: 12,
    maxWidth: 480,
    width: "100%",
    paddingTop: 20,
    margin: "0 auto",
  },
  summary: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    color: "#6b7280",
    fontSize: 13,
    fontWeight: 700,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 999,
    background: "#6b7280",
    marginRight: 4,
  },
  nextBtn: {
    background: "#5c5470",
    color: "#fff",
    border: 0,
    borderRadius: 22,
    padding: "12px 20px",
    fontWeight: 700,
    fontSize: 16,
  },
  backBtn: {
    background: "#e7e9ee",
    color: "#334155",
    border: 0,
    borderRadius: 22,
    padding: "12px 18px",
    fontWeight: 700,
    fontSize: 15,
  },
};
